'use client';

import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';

interface PreviewListenerProps {
  children: React.ReactNode;
}

interface StrapiMessage {
  type: string;
  data?: Record<string, unknown>;
  timestamp?: number;
  contentType?: string;
  documentId?: string;
}

export default function PreviewListener({ children }: PreviewListenerProps) {
  const _router = useRouter();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  // Simple refresh function
  const refreshPage = useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('PreviewListener: Refreshing page...');
    setIsRefreshing(true);

    // Force a hard refresh to ensure fresh content
    window.location.reload();

    setLastUpdate(new Date());
    setTimeout(() => setIsRefreshing(false), 2000);
  }, []);

  // Debounced refresh function
  const debouncedRefresh = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        refreshPage();
      }, 500); // 500ms debounce
    };
  }, [refreshPage]);

  useEffect(() => {
    const handleMessage = (message: MessageEvent<StrapiMessage>) => {
      try {
        // eslint-disable-next-line no-console
        console.log('PreviewListener: Received message:', {
          origin: message.origin,
          data: message.data,
          type: message.data?.type,
        });

        // Check if the message is from Strapi
        const strapiUrl = process.env.NEXT_PUBLIC_STRAPI_URL;
        if (!strapiUrl) {
          // eslint-disable-next-line no-console
          console.warn(
            'PreviewListener: NEXT_PUBLIC_STRAPI_URL not configured'
          );
          return;
        }

        // Accept messages from Strapi or localhost (for development)
        const allowedOrigins = [
          strapiUrl,
          'http://localhost:1337',
          'https://localhost:1337',
        ];

        if (!allowedOrigins.includes(message.origin)) {
          // eslint-disable-next-line no-console
          console.log(
            'PreviewListener: Ignoring message from unauthorized origin:',
            message.origin
          );
          return;
        }

        // Handle different message types
        if (message.data?.type === 'strapiUpdate') {
          // eslint-disable-next-line no-console
          console.log(
            'PreviewListener: Received Strapi update, triggering refresh'
          );
          debouncedRefresh();
        } else if (message.data?.type === 'strapiPreview') {
          // eslint-disable-next-line no-console
          console.log('PreviewListener: Received Strapi preview message');
          debouncedRefresh();
        } else if (message.data?.type === 'strapiSave') {
          // eslint-disable-next-line no-console
          console.log('PreviewListener: Content saved in Strapi');
          debouncedRefresh();
        } else {
          // eslint-disable-next-line no-console
          console.log(
            'PreviewListener: Unknown message type:',
            message.data?.type
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('PreviewListener: Error handling message', error);
      }
    };

    // Add the event listener
    window.addEventListener('message', handleMessage);

    // eslint-disable-next-line no-console
    console.log('PreviewListener: Active and listening for Strapi updates');
    // eslint-disable-next-line no-console
    console.log(
      'PreviewListener: Strapi URL:',
      process.env.NEXT_PUBLIC_STRAPI_URL
    );

    // Cleanup the event listener on unmount
    return () => {
      window.removeEventListener('message', handleMessage);
      // eslint-disable-next-line no-console
      console.log('PreviewListener: Cleaned up event listener');
    };
  }, [debouncedRefresh]);

  return (
    <>
      {children}

      {/* Visual feedback for preview updates */}
      <div
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
          pointerEvents: 'none',
        }}
      >
        {isRefreshing && (
          <div
            style={{
              background: '#4CAF50',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              animation: 'fadeInOut 2s ease-in-out',
            }}
          >
            🔄 Refreshing preview...
          </div>
        )}

        {lastUpdate && !isRefreshing && (
          <div
            style={{
              background: '#2196F3',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              fontSize: '12px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              animation: 'fadeInOut 3s ease-in-out',
            }}
          >
            ✅ Preview updated at {lastUpdate.toLocaleTimeString()}
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          20% {
            opacity: 1;
            transform: translateY(0);
          }
          80% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-10px);
          }
        }
      `}</style>
    </>
  );
}
